import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from '../../../../../components/PhoneInput';
import Input from '../../../../../components/Input';
import Switch from '../../../../../components/Switch';
import Dropdown from '../../../../../components/Dropdown';
import TextArea from '../../../../../components/TextArea';
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import { Validation } from '../../../../../interfaces/common';
import { AddEdit as AddEditInterface } from '../../../../../interfaces/user.interface';

interface UserDetailsProps {
  edit: boolean;
  view?: boolean;
  roles: any[];
  allRoles: any[];
  salutations: any[];
  allSalutations: any[];
  cities: any[];
  allCities: any[];
  states: any[];
  allStates: any[];
  formData: AddEditInterface;
  validation: Validation;
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
}

const UserDetails: React.FC<UserDetailsProps> = ({
  edit,
  view,
  roles,
  allRoles,
  salutations,
  allSalutations,
  cities,
  allCities,
  states,
  allStates,
  formData,
  validation,
  dropdownSearchData,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange,
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <form className="card-body" autoComplete="off">
      <div className="row g-6">
        <h6 className="mb-0">1. {t('users.addEdit.pageTexts.form.sections.primaryDetails')}</h6>

        <div className="col-md-2">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.salutation ? [formData.salutation] : []}
              required
              options={salutations.map((s: any) => ({ label: s.name, value: s.id }))}
              allOptions={allSalutations.map((s: any) => ({ label: s.name, value: s.id }))}
              id={VALIDATION_FIELD_MAPPING.salutation}
              name={VALIDATION_FIELD_MAPPING.salutation}
              disabled={view}
              searchText={dropdownSearchData.salutation}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.salutation.placeholder')}
              label={t('users.addEdit.pageTexts.form.salutation.label')}
              error={!!validation.fields.find((f) => f.field === VALIDATION_FIELD_MAPPING.salutation)}
              errorMessage={validation.fields.find((f) => f.field === VALIDATION_FIELD_MAPPING.salutation)?.message ?? ''}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>
        <div className="col-md-5">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.firstName}
              name={VALIDATION_FIELD_MAPPING.firstName}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.firstName)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.firstName)?.message ?? ''}
              label={t('users.addEdit.pageTexts.form.firstName.label')}
              placeholder={t('users.addEdit.pageTexts.form.firstName.placeholder')}
              value={formData.firstName}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.firstName)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.firstName)}
            />
          </div>
        </div>
        <div className="col-md-5">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.lastName}
              name={VALIDATION_FIELD_MAPPING.lastName}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.lastName)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.lastName)?.message ?? ''}
              label={t('users.addEdit.pageTexts.form.lastName.label')}
              placeholder={t('users.addEdit.pageTexts.form.lastName.placeholder')}
              value={formData.lastName}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.lastName)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.lastName)}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="input-group input-group-merge">
            <div className="form-floating form-floating-outline">
              <Input
                required
                type="text"
                id={VALIDATION_FIELD_MAPPING.email}
                name={VALIDATION_FIELD_MAPPING.email}
                disabled={edit || view}
                error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.email)}
                errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.email)?.message ?? ''}
                label={t('users.addEdit.pageTexts.form.email.label')}
                placeholder={t('users.addEdit.pageTexts.form.email.placeholder')}
                value={formData.email}
                onKeyDown={onKeyDown}
                onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.email)}
                onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.email)}
              />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              required
              defaultCountry="CA"
              id={VALIDATION_FIELD_MAPPING.contactNumber}
              name={VALIDATION_FIELD_MAPPING.contactNumber}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.contactNumber)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.contactNumber)?.message ?? ''}
              label={t('users.addEdit.pageTexts.form.phoneNumber.label')}
              placeholder={t('users.addEdit.pageTexts.form.phoneNumber.placeholder')}
              value={(formData.countryCode || '') + (formData.contactNumber || '')}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.contactNumber)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.contactNumber)}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.role ? [formData.role] : []}
              required
              options={(roles || []).map((role: any) => ({ label: role.name, value: role.id }))}
              allOptions={(allRoles || []).map((role: any) => ({ label: role.name, value: role.id }))}
              id={VALIDATION_FIELD_MAPPING.role}
              name={VALIDATION_FIELD_MAPPING.role}
              disabled={view}
              searchText={dropdownSearchData.role}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.role.placeholder')}
              label={t('users.addEdit.pageTexts.form.role.label')}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.role)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.role)?.message ?? ''}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        {!edit && !view && (
          <div className="col-md-6">
            <div className="form-password-toggle">
              <div className="input-group input-group-merge">
                <div className="form-floating form-floating-outline">
                  <Input
                    required
                    type={showPassword ? 'text' : 'password'}
                    id={VALIDATION_FIELD_MAPPING.password}
                    name={VALIDATION_FIELD_MAPPING.password}
                    error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.password)}
                    errorMessage={
                      validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.password)?.message ?? ''
                    }
                    label={t('users.addEdit.pageTexts.form.password.label')}
                    placeholder={t('users.addEdit.pageTexts.form.password.placeholder')}
                    value={formData.password}
                    onKeyDown={onKeyDown}
                    onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.password)}
                    onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.password)}
                  />
                </div>
                <span
                  className="password-eye-icon input-group-text cursor-pointer"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  <i className={showPassword ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                </span>
              </div>
            </div>
          </div>
        )}

      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">2. {t('users.addEdit.pageTexts.form.sections.addressDetails')}</h6>

        <div className="col-md-12">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.address}
              name={VALIDATION_FIELD_MAPPING.address}
              disabled={view}
              label={t('users.addEdit.pageTexts.form.address.label')}
              placeholder={t('users.addEdit.pageTexts.form.address.placeholder')}
              value={formData.address}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.address)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.address)}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.city ? [formData.city] : []}
              options={cities.map((c: any) => ({ label: c.name, value: c.id }))}
              allOptions={allCities.map((c: any) => ({ label: c.name, value: c.id }))}
              id={VALIDATION_FIELD_MAPPING.city}
              name={VALIDATION_FIELD_MAPPING.city}
              disabled={view}
              searchText={dropdownSearchData.city}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.city.placeholder')}
              label={t('users.addEdit.pageTexts.form.city.label')}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.state ? [formData.state] : []}
              options={states.map((s: any) => ({ label: s.name, value: s.id }))}
              allOptions={allStates.map((s: any) => ({ label: s.name, value: s.id }))}
              id={VALIDATION_FIELD_MAPPING.state}
              name={VALIDATION_FIELD_MAPPING.state}
              disabled={view}
              searchText={dropdownSearchData.state}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.state.placeholder')}
              label={t('users.addEdit.pageTexts.form.state.label')}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.pincode}
              name={VALIDATION_FIELD_MAPPING.pincode}
              disabled={view}
              label={t('users.addEdit.pageTexts.form.pincode.label')}
              placeholder={t('users.addEdit.pageTexts.form.pincode.placeholder')}
              value={formData.pincode}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.pincode)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.pincode)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">3. {t('users.addEdit.pageTexts.form.sections.otherInformation')}</h6>

        <div className="col-md-12 d-flex align-items-start">
          <div className="form-floating form-floating-outline">
            <Switch
              label={
                formData.status
                  ? t('users.addEdit.pageTexts.form.status.activeLabel')
                  : t('users.addEdit.pageTexts.form.status.inActiveLabel')
              }
              checked={formData.status}
              name={VALIDATION_FIELD_MAPPING.status}
              disabled={view}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.status)}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default UserDetails;
